import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import arthereumProvider from "../../backend/ArthereumProvider";
import { UserModelData } from "../../types";
import { actions } from "../redux";
import piNetworkAdsManager, { RewardedAdResult } from "../utils/PiNetworkAdsManager";
import {
  ADS_DAILY_ATH_CAP,
  AD_LADDER_BONUS,
  AD_LADDER_BONUS_AT,
  adRewardForCount,
} from "../utils/rewardTables";

/** Turbo lasts one hour once the meter fills (mirrors `rewardAd` on the backend). */
const TURBO_DURATION_SECONDS = 60 * 60;

/** Backend bucket every Ad Hub ad is credited under (cap = ADS_DAILY_ATH_CAP). */
const ADS_BUCKET = "watch_and_earn";

const todayKey = () => new Date().toISOString().split("T")[0];

export interface AdHubReward {
  /** ATH actually credited for the last ad (0 once the daily ATH cap is hit). */
  ath: number;
  turboActivated: boolean;
  adsToday: number;
}

export interface AdHub {
  /** Verified ads watched today across every placement. */
  adsToday: number;
  /** ATH credited today from ads only. */
  athToday: number;
  /** Turbo meter, 0 → TURBO_ADS_REQUIRED. */
  turboCount: number;
  turboActive: boolean;
  /** Whole minutes left on an active Turbo (0 when inactive). */
  turboMinutesLeft: number;
  /** ATH the next ad pays — 0 once the daily ATH cap is reached. */
  nextAdAth: number;
  /** True when the next ad is the one that also pays AD_LADDER_BONUS. */
  nextIsLadderBonus: boolean;
  athCapReached: boolean;
  isWatching: boolean;
  lastReward: AdHubReward | null;
  /** Last failure surfaced by the ads manager (dev / outside Pi Browser). */
  error: string | null;
  /** Shows one rewarded ad and credits the ladder reward for it. */
  watchAd: () => Promise<void>;
}

/**
 * Single source of truth for every Ad Hub surface (page, Home card, toasts).
 *
 * The NUMBER of ads is never capped — only the ATH minted per day is. Past the
 * ATH cap an ad still charges Turbo, so `watchAd` stays available forever.
 */
const useAdHub = (): AdHub => {
  const dispatch = useDispatch();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  // Kept in a ref so the async credit path always patches the freshest user.
  const userDataRef = useRef(userData);
  userDataRef.current = userData;

  const [isWatching, setIsWatching] = useState(false);
  const [lastReward, setLastReward] = useState<AdHubReward | null>(null);
  const [error, setError] = useState<string | null>(null);

  const today = todayKey();
  const adsToday = user?.adsTodayDate === today ? user?.adsTodayCount || 0 : 0;
  const athToday = user?.lastAdRewardDate === today ? user?.todayAdRewards || 0 : 0;
  const turboCount = user?.turboChargeCount || 0;
  const turboExpirySeconds = user?.turboChargeExpiry?.seconds ?? 0;
  const turboActive = turboExpirySeconds * 1000 > Date.now();
  const turboMinutesLeft = turboActive
    ? Math.max(0, Math.ceil((turboExpirySeconds * 1000 - Date.now()) / 60000))
    : 0;

  const athCapReached = athToday >= ADS_DAILY_ATH_CAP;
  const nextAdAth = athCapReached ? 0 : adRewardForCount(adsToday + 1);
  const nextIsLadderBonus = adsToday + 1 === AD_LADDER_BONUS_AT;

  /** Credits one verified ad and patches Redux with what the backend returned. */
  const creditAd = useCallback(
    async (userId: string, result: RewardedAdResult) => {
      // The ATH of THIS ad is derived from the count AFTER it, so two ads that
      // race can never be paid the same rung of the ladder.
      const ath =
        adRewardForCount(result.adsToday) +
        (result.adsToday === AD_LADDER_BONUS_AT ? AD_LADDER_BONUS : 0);

      let credited = 0;
      let todayTotal: number | undefined;
      try {
        const outcome = await arthereumProvider.awardGameATH(userId, ADS_BUCKET, ath, 1);
        credited = outcome?.credited ?? 0;
        todayTotal = outcome?.todayTotal;
      } catch (err) {
        console.error("[AdHub] Failed to credit ad ATH:", err);
      }

      const current = userDataRef.current;
      const currentUser = current?.user;
      const now = todayKey();
      const localTotal =
        (currentUser?.lastAdRewardDate === now ? currentUser?.todayAdRewards || 0 : 0) +
        credited;
      const patchedUser: any = {
        ...currentUser,
        arthereumBalance: (currentUser?.arthereumBalance || 0) + credited,
        adsTodayDate: now,
        adsTodayCount: result.adsToday,
        lastAdRewardDate: now,
        todayAdRewards: todayTotal ?? localTotal,
        turboChargeCount: result.turboCount,
      };

      if (result.turboActivated) {
        // The backend extends an already-running Turbo instead of restarting it.
        const base = Math.max(Date.now() / 1000, currentUser?.turboChargeExpiry?.seconds || 0);
        patchedUser.turboChargeExpiry = { seconds: base + TURBO_DURATION_SECONDS };
      }

      dispatch(actions.setActualUser({ ...current, user: patchedUser }));
      setLastReward({
        ath: credited,
        turboActivated: result.turboActivated,
        adsToday: result.adsToday,
      });
    },
    [dispatch]
  );

  const watchAd = useCallback(async () => {
    const userId = user?.id;
    if (!userId || isWatching) return;

    setIsWatching(true);
    setError(null);
    // `onSuccess` is not awaited by the manager, so the credit work is kept
    // here and awaited before `isWatching` is released.
    const pending: { work?: Promise<void> } = {};
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(userId, (result) => {
        if (!result) return;
        pending.work = creditAd(userId, result);
      });
      await pending.work;
    } catch (err: any) {
      // Ads only run inside the Pi Browser — in development the manager alerts
      // and this keeps the UI usable instead of leaving it stuck on "loading".
      console.error("[AdHub] Rewarded ad failed:", err);
      setError(
        process.env.NODE_ENV === "development"
          ? "Ads only run inside the Pi Browser."
          : err?.message || "The ad could not be played. Please try again."
      );
    } finally {
      setIsWatching(false);
    }
  }, [user?.id, isWatching, creditAd]);

  return {
    adsToday,
    athToday,
    turboCount,
    turboActive,
    turboMinutesLeft,
    nextAdAth,
    nextIsLadderBonus,
    athCapReached,
    isWatching,
    lastReward,
    error,
    watchAd,
  };
};

export default useAdHub;
